import { MbDialog } from '@am/moebius';
import {
  Component,
  Input,
  NgZone,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { AnalyticsService } from '@core/services/analytics.service';
import { select, Store } from '@ngrx/store';
import { UrlService } from '@services/url.service';
import { selectContextCore } from '@store/context/context.selector';
import { IHotelsAppState } from '@store/index';
import { fromEvent, interval, merge, Observable, Subject, Subscription } from 'rxjs';
import { skipWhile, switchMap, take, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'am-user-inactivity',
  // Styles in global
  templateUrl: './user-inactivity.component.html',
})
export class UserInactivityComponent implements OnInit {
  @Input()
  public hotel: any;
  @ViewChild('userInactivityDialog', { static: true })
  public userInactivityDialog: TemplateRef<any>;

  public resizedUrl: string;
  public imageUrl: string;

  public inactivityTimeInSeconds = 480;
  public inactivityEvents: Array<any>[] = [
    [document, 'click'],
    [document, 'mousemove'],
    [document, 'keypress'],
  ];

  public mergedEventsObservable$: Observable<any>;
  public eventsObserveable$: Observable<any>;
  public observableSubscription: Subscription;
  private unSubscribe$ = new Subject<void>();
  private brands: Array<string> = ['avantrip'];
  public brand: string;

  constructor(
    private vcr: ViewContainerRef,
    private dialog: MbDialog,
    private urlService: UrlService,
    private ngZone: NgZone,
    private analyticsService: AnalyticsService,
    private storeContext$: Store<IHotelsAppState>,
  ) {
    this.storeContext$
      .pipe(select(selectContextCore), takeUntil(this.unSubscribe$))
      .subscribe((context) => {
        if (this.brands.includes(context.brand)) {
          this.brand = `${context.brand}`;
        } else {
          this.brand = 'almundo';
        }
      });
  }

  public ngOnInit() {
    if (this.hotel) {
      this.resizedUrl = this.urlService.getResizedUrl();
      this.initObservable();
    }
    if (this.hotel.images?.length > 0) {
      this.imageUrl = this.hotel?.images[0].url.replace('st.images', 'images');
    }
  }

  public initObservable() {
    const observableArray$: Observable<any>[] = [];
    this.inactivityEvents.forEach((x) => {
      observableArray$.push(fromEvent(x[0], x[1]));
    });
    this.mergedEventsObservable$ = merge(...observableArray$);
    this.createObservable();
  }

  public createObservable() {
    this.ngZone.runOutsideAngular(() => {
      this.eventsObserveable$ = this.mergedEventsObservable$.pipe(
        switchMap(() => interval(1000).pipe(take(this.inactivityTimeInSeconds))),
        tap((second) => this.openDialog(second)),
        skipWhile((x) => {
          return x !== this.inactivityTimeInSeconds - 1;
        }),
      );
      this.subscribeObservable();
    });
  }

  public subscribeObservable() {
    this.observableSubscription = this.eventsObserveable$.subscribe(() => {
      this.observableSubscription.unsubscribe();
    });
  }

  public openDialog(second: number) {
    if (second === this.inactivityTimeInSeconds - 1) {
      this.trackInactivityEvent();
      this.dialog.open(this.userInactivityDialog, {
        closeOnBackdropClick: true,
        closeOnEscKey: true,
        dialogData: { hotel: this.hotel } as any,
        viewContainerRef: this.vcr,
      });
    }
  }

  public detailRedirect() {
    this.trackDetailRedirect();
    const redirection = this.urlService.getDetailRedirection(this.hotel);
    window.open(redirection.url, redirection.self ? '_self' : '_blank');
  }

  private trackInactivityEvent() {
    this.analyticsService.trackEvent({
      action: 'User Inactivity',
      label: 'Show',
      nonInteraction: true,
      section: 'Results',
    });
  }

  private trackDetailRedirect() {
    this.analyticsService.trackEvent({
      action: 'User Inactivity',
      label: 'Go To Detail',
      section: 'Results',
    });
  }
}
